import { EChartsOption } from 'echarts';
import {
  formatStatsPeriodLabel,
  formatUtcDateOnly,
} from '../admin-stats-period/admin-stats-period.helpers';
import { ReportMetricsDto, ReportsPeriodPreset } from './reports.types';

const DAY_MS = 24 * 60 * 60 * 1000;

const COMPARISON_METRICS = [
  { key: 'creatorsJoined' as const, label: 'Creators joined' },
  { key: 'briefsPosted' as const, label: 'Briefs posted' },
  { key: 'creatorsHired' as const, label: 'Creators hired' },
  { key: 'brandsJoined' as const, label: 'Brands joined' },
];

export function getLastMonthRangeUtc(now = new Date()): { startDate: string; endDate: string } {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const start = new Date(Date.UTC(year, month - 1, 1));
  const end = new Date(Date.UTC(year, month, 0));

  return {
    startDate: formatUtcDateOnly(start),
    endDate: formatUtcDateOnly(end),
  };
}

export function getLastQuarterRangeUtc(now = new Date()): { startDate: string; endDate: string } {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const currentQuarterStartMonth = Math.floor(month / 3) * 3;
  const lastQuarterEnd = new Date(Date.UTC(year, currentQuarterStartMonth, 0));
  const lastQuarterStart = new Date(
    Date.UTC(lastQuarterEnd.getUTCFullYear(), lastQuarterEnd.getUTCMonth() - 2, 1),
  );

  return {
    startDate: formatUtcDateOnly(lastQuarterStart),
    endDate: formatUtcDateOnly(lastQuarterEnd),
  };
}

export function calculateInclusiveDays(startDate: string, endDate: string): number {
  const start = new Date(`${startDate}T00:00:00.000Z`).getTime();
  const end = new Date(`${endDate}T00:00:00.000Z`).getTime();
  return Math.round((end - start) / DAY_MS) + 1;
}

/** Calendar-aware previous window for presets; equal-length fallback for custom ranges. */
export function resolveComparisonPeriod(
  startDate: string,
  endDate: string,
  preset: ReportsPeriodPreset,
): { startDate: string; endDate: string } {
  if (preset === 'last-quarter') {
    const start = new Date(`${startDate}T00:00:00.000Z`);
    const previousEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 0));
    const previousStart = new Date(
      Date.UTC(previousEnd.getUTCFullYear(), previousEnd.getUTCMonth() - 2, 1),
    );
    return {
      startDate: formatUtcDateOnly(previousStart),
      endDate: formatUtcDateOnly(previousEnd),
    };
  }

  if (preset === 'last-month') {
    const start = new Date(`${startDate}T00:00:00.000Z`);
    const previousEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 0));
    const previousStart = new Date(
      Date.UTC(previousEnd.getUTCFullYear(), previousEnd.getUTCMonth(), 1),
    );
    return {
      startDate: formatUtcDateOnly(previousStart),
      endDate: formatUtcDateOnly(previousEnd),
    };
  }

  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const spanDays = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
  const previousEnd = new Date(start.getTime() - DAY_MS);
  const previousStart = new Date(previousEnd.getTime() - (spanDays - 1) * DAY_MS);

  return {
    startDate: formatUtcDateOnly(previousStart),
    endDate: formatUtcDateOnly(previousEnd),
  };
}

export function buildPeriodComparisonBarChart(
  current: ReportMetricsDto,
  previous: ReportMetricsDto,
  isDarkMode: boolean,
): { chartOption: EChartsOption; chartXaxisData: string[]; hasData: boolean } {
  const chartXaxisData = COMPARISON_METRICS.map((metric) => metric.label);
  const previousValues = COMPARISON_METRICS.map((metric) => previous[metric.key]);
  const currentValues = COMPARISON_METRICS.map((metric) => current[metric.key]);
  const hasData = [...previousValues, ...currentValues].some((value) => value > 0);

  const previousLabel = formatStatsPeriodLabel(previous.startDate, previous.endDate);
  const currentLabel = formatStatsPeriodLabel(current.startDate, current.endDate);
  const previousColor = isDarkMode ? '#818cf8' : '#6366f1';
  const currentColor = isDarkMode ? '#e879f9' : '#d946ef';

  const chartOption: EChartsOption = {
    legend: {
      top: 0,
      left: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: {
        color: isDarkMode ? '#a1a1aa' : '#71717a',
        fontSize: 12,
      },
      data: [previousLabel, currentLabel],
    },
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '3%', bottom: '8%', top: '18%', containLabel: true },
    xAxis: { type: 'category', data: chartXaxisData },
    yAxis: { type: 'value' },
    series: [
      {
        name: previousLabel,
        type: 'bar',
        data: previousValues,
        barGap: '20%',
        itemStyle: { color: previousColor },
      },
      {
        name: currentLabel,
        type: 'bar',
        data: currentValues,
        itemStyle: { color: currentColor },
      },
    ],
  };

  return { chartOption, chartXaxisData, hasData };
}
